import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { CardElement } from "@stripe/react-stripe-js";

// Components
import { CentralModal } from "../../../UI";

// Icons
import { I, CardBrand, brandName } from "./icons";

// Styles (scoped under .tf-billing)
import "../../../../styles/billing.css";

/* ---------- Stripe ---------- */
const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
export const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

// Load Inter inside Stripe's iframe so the card field matches the app.
export const ELEMENTS_OPTIONS = {
  fonts: [
    {
      cssSrc:
        "https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap"
    }
  ]
};

export const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "14px",
      fontFamily: '"Inter", sans-serif',
      color: "#000000",
      "::placeholder": { color: "#AAAAAA" }
    },
    invalid: { color: "#BA1A1A", iconColor: "#BA1A1A" }
  }
};

/* Stripe Elements card field styled like the design's input. */
export const StripeCardField = ({ invalid, onChange }) => {
  const [focused, setFocused] = useState(false);
  return (
    <>
      <div
        className={
          "stripe-el" + (focused ? " focus" : "") + (invalid ? " invalid" : "")
        }
      >
        <CardElement
          options={CARD_ELEMENT_OPTIONS}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={onChange}
        />
      </div>
      <div className="stripe-hint">
        <I.Lock width="12" height="12" /> Secured by Stripe
      </div>
    </>
  );
};

/* ---------- Small primitives ---------- */
export const Spinner = ({ dark }) => (
  <span className={"spinner" + (dark ? " dark" : "")} />
);

export const InlineError = ({ children, style }) => (
  <div className="inline-err" style={style}>
    <I.Alert width="15" height="15" /> <span>{children}</span>
  </div>
);

export const Banner = ({ variant = "warn", icon, title, text }) => (
  <div className={"banner banner-" + variant}>
    {icon && <span className="banner-ico">{icon}</span>}
    <div className="banner-body">
      {title && <div className="banner-title">{title}</div>}
      {text && <div className="banner-text">{text}</div>}
    </div>
  </div>
);

/* Selectable saved-card radio row (checkout + change-default modal). */
export const SavedCardRow = ({ card, selected, onSelect }) => (
  <div className={"radiorow" + (selected ? " sel" : "")} onClick={onSelect}>
    <span className="radio-mark">
      <i />
    </span>
    <span className="card-brand">
      <CardBrand brand={card.brand} />
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 13.5,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          gap: 8
        }}
      >
        {brandName(card.brand)} •••• {card.last4}
        {card.isDefault && (
          <span className="badge badge-neutral" style={{ fontSize: 10 }}>
            Default
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: "var(--text-weak)", marginTop: 1 }}>
        Exp {card.expLabel}
      </div>
    </div>
  </div>
);

export const PageHead = ({ eyebrow, title, subtitle }) => (
  <div className="tf-pagehead">
    {eyebrow && <div className="tf-eyebrow">{eyebrow}</div>}
    <h1 className="tf-title">{title}</h1>
    {subtitle && <div className="tf-subtitle">{subtitle}</div>}
  </div>
);

/* Design-styled modal body rendered through the app's CentralModal. */
export const BillingModal = ({
  title,
  sub,
  onClose,
  children,
  footer,
  width = 460
}) => (
  <CentralModal closeModal={onClose} width={width}>
    <div className="tf-billing bmodal">
      {(title || onClose) && (
        <div className="modal-head">
          <div>
            {title && <div className="modal-title">{title}</div>}
            {sub && <div className="modal-sub">{sub}</div>}
          </div>
          {onClose && (
            <button className="modal-x" onClick={onClose} aria-label="Close">
              <I.Close />
            </button>
          )}
        </div>
      )}
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-foot">{footer}</div>}
    </div>
  </CentralModal>
);

/* ---------- Formatting helpers ---------- */
const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});
export const money = cents => USD.format(Math.round(cents / 100));

export const fmtDate = date =>
  new Date(date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });

export const fmtShort = date =>
  new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });

// Subscription periods come from the BE as unix timestamps in seconds.
export const fmtStamp = ts => (ts ? fmtDate(ts * 1000) : "");
export const fmtStampShort = ts => (ts ? fmtShort(ts * 1000) : "");
export const daysUntilStamp = ts =>
  ts ? Math.max(0, Math.ceil((ts * 1000 - Date.now()) / 86400000)) : 0;

export const locationsLabel = allowed => {
  if (!allowed) return null;
  if (allowed >= 999) return "Unlimited locations";
  if (allowed === 1) return "1 location";
  return `Up to ${allowed} locations`;
};

export const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/* Sort plans for display: priced plans by price ascending, contact-only last. */
export const sortPlans = plans =>
  [...plans].sort(
    (a, b) =>
      (a.contactOnly ? 1 : 0) - (b.contactOnly ? 1 : 0) ||
      (a.price?.unitAmount || 0) - (b.price?.unitAmount || 0)
  );

/* Confirm a subscription's client secret with Stripe when the backend asks
   for it ('payment' → confirmCardPayment, 'setup' → confirmCardSetup).
   Returns { ok } or { ok: false, message }. */
export const confirmSubscriptionSecret = async (
  stripe,
  clientSecret,
  clientSecretType,
  paymentMethodId
) => {
  if (!clientSecret) return { ok: true };
  const confirm =
    clientSecretType === "payment"
      ? stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodId
        })
      : stripe.confirmCardSetup(clientSecret, {
          payment_method: paymentMethodId
        });
  const result = await confirm;
  if (result.error) {
    // "setup_intent_unexpected_state" etc. on an already-confirmed intent is fine.
    if (result.error.code === "setup_intent_unexpected_state") {
      return { ok: true };
    }
    return { ok: false, message: result.error.message };
  }
  return { ok: true };
};
